// Controlled edit form for the candidate profile fields. Shared by
// CandidateOnboarding, CandidateDashboard and PBTalentAdmin.
import React from 'react';
import {
  C, TARGET_ROLES, ASSET_CLASSES, SENIORITIES, RELOCATION_COUNTRIES, Seniority,
} from './talentConnect';
import { inputStyle, labelStyle, ChipSelect, TagInput } from './talentUI';

export interface CandidateFormState {
  full_name: string;
  current_role: string;
  current_employer: string;
  hide_employer: boolean;
  description: string;
  years_experience: string;
  seniority: Seniority;
  target_roles: string[];
  asset_classes: string[];
  skills: string[];
  relocation_countries: string[];
}

export const emptyCandidateForm: CandidateFormState = {
  full_name: '', current_role: '', current_employer: '', hide_employer: false,
  description: '', years_experience: '', seniority: 'mid',
  target_roles: [], asset_classes: [], skills: [], relocation_countries: [],
};

// Build the DB payload (with correct types) from the form state.
export function candidatePayload(f: CandidateFormState) {
  return {
    full_name: f.full_name.trim() || null,
    current_role: f.current_role.trim() || null,
    current_employer: f.current_employer.trim() || null,
    hide_employer: f.hide_employer,
    description: f.description.trim() || null,
    years_experience: f.years_experience ? parseInt(f.years_experience) : null,
    seniority: f.seniority,
    target_roles: f.target_roles,
    asset_classes: f.asset_classes,
    skills: f.skills,
    relocation_countries: f.relocation_countries,
  };
}

export function CandidateFields({ form, setForm }: {
  form: CandidateFormState;
  setForm: React.Dispatch<React.SetStateAction<CandidateFormState>>;
}) {
  const set = <K extends keyof CandidateFormState>(k: K, v: CandidateFormState[K]) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (k: 'target_roles' | 'asset_classes' | 'relocation_countries', v: string) =>
    setForm(f => ({ ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v] }));

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div><label style={labelStyle}>Full Name</label><input style={inputStyle} value={form.full_name} onChange={e => set('full_name', e.target.value)} /></div>
        <div><label style={labelStyle}>Years of Experience</label><input type="number" style={inputStyle} value={form.years_experience} onChange={e => set('years_experience', e.target.value)} /></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '10px' }}>
        <div><label style={labelStyle}>Current Role</label><input style={inputStyle} value={form.current_role} onChange={e => set('current_role', e.target.value)} placeholder="e.g. Portfolio Manager" /></div>
        <div><label style={labelStyle}>Current Employer</label><input style={inputStyle} value={form.current_employer} onChange={e => set('current_employer', e.target.value)} /></div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: C.textSec, cursor: 'pointer', marginBottom: '18px' }}>
        <input type="checkbox" checked={form.hide_employer} onChange={e => set('hide_employer', e.target.checked)} />
        Hide my current employer from managers
      </label>

      <div style={{ marginBottom: '18px' }}>
        <label style={labelStyle}>Description / Summary</label>
        <textarea rows={4} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="A short professional summary. Auto-populated from your CV, edit as you like." />
      </div>

      <div style={{ marginBottom: '18px' }}>
        <label style={labelStyle}>Seniority</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {SENIORITIES.map(s => {
            const on = form.seniority === s.value;
            return (
              <button key={s.value} type="button" onClick={() => set('seniority', s.value)} style={{
                padding: '8px 16px', borderRadius: '3px', fontSize: '12.5px', cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 500,
                border: `1px solid ${on ? C.navy : C.border}`, background: on ? C.navy : C.white, color: on ? C.white : C.textSec,
              }}>
                <span style={{ marginRight: '5px' }}>{on ? '●' : '○'}</span>{s.label} <span style={{ opacity: 0.7 }}>({s.hint})</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: '18px' }}>
        <label style={labelStyle}>Target Roles</label>
        <ChipSelect options={TARGET_ROLES} selected={form.target_roles} onToggle={v => toggle('target_roles', v)} />
      </div>

      <div style={{ marginBottom: '18px' }}>
        <label style={labelStyle}>Asset Classes</label>
        <ChipSelect options={ASSET_CLASSES} selected={form.asset_classes} onToggle={v => toggle('asset_classes', v)} />
      </div>

      <div style={{ marginBottom: '18px' }}>
        <label style={labelStyle}>Skills</label>
        <TagInput tags={form.skills} onChange={t => set('skills', t)} />
      </div>

      <div style={{ marginBottom: '4px' }}>
        <label style={labelStyle}>Willing to Relocate To</label>
        <ChipSelect options={RELOCATION_COUNTRIES} selected={form.relocation_countries} onToggle={v => toggle('relocation_countries', v)} columns={3} />
      </div>
    </>
  );
}
